import React from 'react';
import { format, startOfYear, endOfYear, eachDayOfInterval, getDay, isSameDay } from 'date-fns';

/**
 * A GitHub-style heatmap component to visualize user activity over the current year.
 * @param {object} props - The component props.
 * @param {Array<object>} props.data - An array of activity data. Each object should have an '_id' (date string 'yyyy-MM-dd') and 'count'.
 */
const ConsistencyHeatmap = ({ data }) => {
  // Memoize the calculation of weeks and months to avoid re-computing on every render.
  const { weeks, months, totalActivity, streakData } = React.useMemo(() => {
    const today = new Date();
    const yearStart = startOfYear(today);
    const daysInYear = eachDayOfInterval({ start: yearStart, end: endOfYear(today) });

    // Create a Map for quick lookups of counts by date ('yyyy-MM-dd')
    const dataMap = new Map((data || []).map(item => [item._id, item.count]));

    // Calculate total activity and streak data
    let totalActivity = 0;
    let currentStreak = 0;
    let longestStreak = 0;
    let tempStreak = 0;

    // This array will hold all the cells for the grid, including empty placeholders.
    const allCells = [];

    // Add empty placeholders for days before the start of the year in the first week.
    const firstDayOfWeek = getDay(yearStart);
    for (let i = 0; i < firstDayOfWeek; i++) {
      allCells.push(null);
    }

    // Populate the cells with actual day data and calculate streaks.
    daysInYear.forEach(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const count = dataMap.get(dateStr) || 0;
      totalActivity += count;
      
      allCells.push({
        date: dateStr,
        count: count,
      });

      // Calculate streaks
      if (count > 0) {
        tempStreak++;
        longestStreak = Math.max(longestStreak, tempStreak);
        if (isSameDay(date, today) || date < today) {
          currentStreak = tempStreak;
        }
      } else {
        if (date <= today) {
          currentStreak = 0;
        }
        tempStreak = 0;
      }
    });

    // Group all cells into weeks (7 days per week).
    const weeks = [];
    for (let i = 0; i < allCells.length; i += 7) {
      weeks.push(allCells.slice(i, i + 7));
    }

    // Generate month labels based on where each month starts in the grid.
    const months = [];
    let lastMonth = -1;
    weeks.forEach((week, weekIndex) => {
      const firstAvailableDayInWeek = week.find(day => day !== null);
      if (!firstAvailableDayInWeek) return;
      
      const dateForMonth = new Date(firstAvailableDayInWeek.date + 'T00:00:00');
      const monthOfFirstDay = dateForMonth.getMonth();

      if (monthOfFirstDay !== lastMonth) {
        // A simple heuristic to avoid month label overlaps.
        if (months.length === 0 || weekIndex > (months[months.length - 1].week + 3)) {
          months.push({
            week: weekIndex,
            month: format(dateForMonth, 'MMM'),
          });
          lastMonth = monthOfFirstDay;
        }
      }
    });

    return { 
      weeks, 
      months, 
      totalActivity,
      streakData: {
        current: currentStreak,
        longest: longestStreak
      }
    };
  }, [data]);

  // Enhanced color scheme matching the purple-blue gradient theme
  const getIntensity = (count) => {
    const safeCount = count || 0;
    if (safeCount === 0) return 'bg-gray-100 dark:bg-slate-800 border-gray-200/50 dark:border-gray-700/50';
    if (safeCount === 1) return 'bg-purple-100 dark:bg-purple-900/50 border-purple-200/50 dark:border-purple-700/50';
    if (safeCount <= 3) return 'bg-purple-300 dark:bg-purple-700/70 border-purple-300/50 dark:border-purple-600/50';
    if (safeCount <= 5) return 'bg-purple-500 dark:bg-purple-600 border-purple-400/50 dark:border-purple-500/50';
    return 'bg-gradient-to-br from-primary-500 to-primary-600 border-primary-400/50 dark:border-primary-500/50 shadow-sm';
  };

  // Generates the tooltip text for a given day.
  const getTooltipText = (date, count) => {
    const safeCount = count || 0;
    // Parse date as UTC to prevent timezone-related date shifts
    const correctedDate = new Date(date + 'T00:00:00');
    return `${format(correctedDate, 'MMM dd, yyyy')}: ${safeCount} problem${safeCount !== 1 ? 's' : ''} solved`;
  };

  return (
    <div className="card">
      {/* Enhanced Header */}
      <div className="card-header">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Consistency Heatmap
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Daily problem-solving activity throughout {format(new Date(), 'yyyy')}
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-gradient">
              {totalActivity}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Total Activity
            </div>
          </div>
        </div>
      </div>
      
      {/* Chart Container */}
      <div className="card-body">
        <div className="flex flex-col w-full">
          {/* Month Labels - Enhanced styling */}
          <div className="relative h-6 w-full" style={{ paddingLeft: '32px' }}>
            {months.map(({ week, month }) => (
              <div
                key={week}
                className="absolute top-0 text-xs font-medium text-gray-600 dark:text-gray-300"
                style={{ 
                  left: `calc(32px + ${week} * (100% - 32px) / ${weeks.length})`,
                  transform: 'translateX(-50%)'
                }}
              >
                {month}
              </div>
            ))}
          </div>

          <div className="flex w-full mt-1">
            {/* Day Labels - Enhanced styling */}
            <div className="flex flex-col text-xs font-medium text-gray-600 dark:text-gray-300 pr-3 w-8 flex-shrink-0">
              <div className="h-3 mt-0.5"></div> {/* Spacer */}
              <div className="h-3 flex items-center">Mon</div>
              <div className="h-3"></div>
              <div className="h-3 flex items-center">Wed</div>
              <div className="h-3"></div>
              <div className="h-3 flex items-center">Fri</div>
              <div className="h-3"></div>
            </div>

            {/* Heatmap Grid - Enhanced with better visual effects */}
            <div className="flex-1 min-w-0">
              <div className="flex justify-between" style={{ gap: 'calc(100% / 260)' }}>
                {weeks.map((week, weekIndex) => (
                  <div key={weekIndex} className="flex flex-col" style={{ gap: '1px' }}>
                    {week.map((day, dayIndex) => {
                      if (!day) {
                        return (
                          <div 
                            key={dayIndex} 
                            className="w-3 h-3" 
                            style={{ 
                              width: 'max(10px, min(14px, calc(100vw / 60)))',
                              height: 'max(10px, min(14px, calc(100vw / 60)))'
                            }}
                          />
                        );
                      }
                      const isToday = isSameDay(new Date(day.date + 'T00:00:00'), new Date());
                      return (
                        <div
                          key={day.date}
                          className={`rounded-sm border ${getIntensity(day.count)} ${
                            isToday ? 'ring-2 ring-primary-500 ring-offset-1 dark:ring-offset-gray-900' : ''
                          } cursor-pointer hover:scale-110 hover:z-10 transition-all duration-200 ease-in-out hover:shadow-lg hover:shadow-primary-500/20`}
                          style={{ 
                            width: 'max(10px, min(14px, calc(100vw / 60)))',
                            height: 'max(10px, min(14px, calc(100vw / 60)))'
                          }}
                          title={getTooltipText(day.date, day.count)}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Overlay gradient for visual enhancement */}
          <div className="absolute inset-0 pointer-events-none rounded-2xl bg-gradient-to-t from-white/5 to-transparent dark:from-gray-900/10"></div>
        </div>

        {/* Enhanced Legend */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
          <div className="text-xs font-medium text-gray-600 dark:text-gray-300">
            {format(new Date(), 'yyyy')} Activity
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Less</span>
            <div className="flex gap-1">
              <div className="w-3 h-3 bg-gray-100 dark:bg-slate-800 rounded-sm border border-gray-300/50 dark:border-gray-600/50"></div>
              <div className="w-3 h-3 bg-purple-100 dark:bg-purple-900/50 rounded-sm border border-purple-200/50 dark:border-purple-700/50"></div>
              <div className="w-3 h-3 bg-purple-300 dark:bg-purple-700/70 rounded-sm border border-purple-300/50 dark:border-purple-600/50"></div>
              <div className="w-3 h-3 bg-purple-500 dark:bg-purple-600 rounded-sm border border-purple-400/50 dark:border-purple-500/50"></div>
              <div className="w-3 h-3 bg-gradient-to-br from-primary-500 to-primary-600 rounded-sm border border-primary-400/50 dark:border-primary-500/50 shadow-sm"></div>
            </div>
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">More</span>
          </div>
        </div>

        {/* Enhanced Stats Footer */}
        <div className="mt-4 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="flex items-center justify-center space-x-2">
                <div className="w-2 h-2 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"></div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Current Streak</span>
              </div>
              <div className="text-xl font-bold text-gradient mt-1">
                {streakData.current}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">days</div>
            </div>
            <div>
              <div className="flex items-center justify-center space-x-2">
                <div className="w-2 h-2 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full"></div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Longest Streak</span>
              </div>
              <div className="text-xl font-bold text-gradient mt-1">
                {streakData.longest}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">days</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsistencyHeatmap;